// Single entry point for every backend function call made from the browser.
//
// Raw supabase.functions.invoke() has two failure modes that burned us:
//   1. A stale/expired access token gets rejected at the gateway, before the
//      function runs. That rejection carries no CORS headers, so the browser
//      reports the useless "Failed to send a request to the Edge Function".
//   2. The real error body is hidden inside error.context and never surfaced,
//      so a readable server message shows up as a generic network string.
//
// This wrapper refreshes the session ahead of time, retries once on an auth
// failure, and always digs out the function's actual response body.

import { supabase } from "@/integrations/supabase/client";
import { reportClientError } from "./telegram-alert";
import { toast } from "sonner";

export type InvokeOptions = {
  body?: unknown;
  headers?: Record<string, string>;
  method?: "POST" | "GET" | "PUT" | "PATCH" | "DELETE";
  /** Set false for endpoints that legitimately work signed-out (consent, status). */
  requireAuth?: boolean;
  /** Skip the failure-burst alerting (used by the alert path itself). */
  silent?: boolean;
  /**
   * Called before each transient retry (backend degraded / network blip), so
   * the caller can show "Backend busy, retrying…" instead of a hard error.
   */
  onRetry?: (attempt: number, detail: string) => void;
  /**
   * Opt out of transient retries for calls that are NOT safe to repeat (a
   * retry that lands twice would charge twice). Calls that carry their own
   * idempotency key — like start-job with a stable requestId — can keep
   * retrying safely and should leave this alone.
   */
  noTransientRetry?: boolean;
};


export class InvokeError extends Error {
  constructor(
    message: string,
    public readonly fn: string,
    public readonly status?: number,
    public readonly isAuthError = false,
  ) {
    super(message);
    this.name = "InvokeError";
  }
}

const REFRESH_MARGIN_MS = 90_000;

async function ensureFreshSession(): Promise<{ ok: boolean; reason?: string }> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return { ok: false, reason: "no-session" };
  const expiresAt = (session.expires_at ?? 0) * 1000;
  if (expiresAt - Date.now() < REFRESH_MARGIN_MS) {
    const { error } = await supabase.auth.refreshSession();
    if (error) return { ok: false, reason: error.message };
  }
  return { ok: true };
}

/** Pull the real response body out of a FunctionsHttpError. */
async function extractDetail(error: unknown, fallback: string): Promise<{ detail: string; status?: number }> {
  const ctx = (error as { context?: Response })?.context;
  let status: number | undefined;
  if (ctx && typeof ctx === "object" && "status" in ctx) status = (ctx as Response).status;
  if (ctx && typeof (ctx as Response).text === "function") {
    const raw = await (ctx as Response).clone().text().catch(() => "");
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const msg = parsed.error ?? parsed.message ?? parsed.detail;
        if (typeof msg === "string" && msg) return { detail: msg, status };
      } catch {
        return { detail: raw.slice(0, 400), status };
      }
    }
  }
  return { detail: fallback, status };
}

const AUTH_SIGNALS = [
  "jwt expired",
  "jwt is expired",
  "invalid jwt",
  "missing authorization",
  "unauthorized",
  "token is expired",
  "invalid claim",
];

function looksLikeAuthFailure(detail: string, status?: number): boolean {
  if (status === 401 || status === 403) return true;
  const d = detail.toLowerCase();
  return AUTH_SIGNALS.some((s) => d.includes(s));
}

// A degraded backend (Cloud having a bad minute) is not the user's problem —
// retry it quietly before surfacing anything.
const TRANSIENT_SIGNALS = [
  "service_degraded",
  "service is temporarily unavailable",
  "temporarily unavailable",
  "failed to fetch",
  "failed to send a request",
  "network error",
  "load failed",
  "connection reset",
  // Cloudflare 52x pages served when the backend didn't answer properly.
  "web server is returning an unknown error",
  "web server is down",
  "connection timed out",
  "origin is unreachable",
  "a timeout occurred",
];

const TRANSIENT_RETRY_DELAYS_MS = [1_000, 3_000];

export function looksLikeTransientFailure(detail: string, status?: number): boolean {
  if (status === 502 || status === 503 || status === 504) return true;
  if (typeof status === "number" && status >= 520 && status <= 524) return true;
  const d = detail.toLowerCase();
  return TRANSIENT_SIGNALS.some((s) => d.includes(s));
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Default user-facing notice while we retry a degraded backend. Callers can
// override with their own onRetry handler.
let lastRetryToast = 0;
function defaultRetryNotice(): void {
  const now = Date.now();
  if (now - lastRetryToast < 5_000) return;
  lastRetryToast = now;
  toast.loading("Backend busy — retrying…", { id: "backend-retry", duration: 4_000 });
}

// A single user hitting one bad call is normal. The same path failing over and
// over means something is actually down — escalate that before a customer emails.
const BURST_WINDOW_MS = 3 * 60 * 1000;
const BURST_THRESHOLD = 3;
const failures = new Map<string, number[]>();
let lastBurstAlert = 0;

function recordFailure(fn: string, detail: string): void {
  const now = Date.now();
  const list = (failures.get(fn) ?? []).filter((t) => now - t < BURST_WINDOW_MS);
  list.push(now);
  failures.set(fn, list);
  if (list.length >= BURST_THRESHOLD && now - lastBurstAlert > BURST_WINDOW_MS) {
    lastBurstAlert = now;
    failures.set(fn, []);
    reportClientError({
      context: `🔁 Repeated backend call failures: ${fn}`,
      message: `${list.length} failures in 3 minutes calling "${fn}". Last error: ${detail}`,
      meta: { fn, count: list.length },
    });
  }
}

/**
 * Call a backend function. Throws InvokeError with a human-readable message.
 */
export async function invokeFunction<T = unknown>(
  fn: string,
  options: InvokeOptions = {},
): Promise<T> {
  const { requireAuth = true, silent = false, onRetry, noTransientRetry = false, ...rest } = options;

  if (requireAuth) {
    const session = await ensureFreshSession();
    if (!session.ok) {
      throw new InvokeError("Session expired — please sign in again.", fn, 401, true);
    }
  }

  const call = async () =>
    supabase.functions.invoke<T>(fn, {
      body: rest.body as Record<string, unknown> | undefined,
      headers: rest.headers,
      method: rest.method,
    });

  let { data, error } = await call();

  // Transient backend degradation: retry on a short ladder before the user
  // ever sees a failure. Skipped for calls flagged as unsafe to repeat.
  const retryDelays = noTransientRetry ? [] : TRANSIENT_RETRY_DELAYS_MS;
  for (let attempt = 0; error && attempt < retryDelays.length; attempt++) {
    const probe = await extractDetail(error, error.message || "Request failed");
    if (!looksLikeTransientFailure(probe.detail, probe.status)) break;
    if (onRetry) onRetry(attempt + 1, probe.detail);
    else if (!silent) defaultRetryNotice();
    await sleep(retryDelays[attempt]);
    ({ data, error } = await call());
  }

  if (!error) toast.dismiss("backend-retry");

  if (error) {
    const { detail, status } = await extractDetail(error, error.message || "Request failed");
    // One retry after a forced refresh: covers the token that expired between
    // our check and the request actually landing.
    if (requireAuth && looksLikeAuthFailure(detail, status)) {
      const refreshed = await supabase.auth.refreshSession();
      if (!refreshed.error && refreshed.data.session) {
        ({ data, error } = await call());
        if (!error) return data as T;
        const second = await extractDetail(error, error.message || "Request failed");
        if (!silent) recordFailure(fn, second.detail);
        throw new InvokeError(second.detail, fn, second.status, looksLikeAuthFailure(second.detail, second.status));
      }
      throw new InvokeError("Session expired — please sign in again.", fn, 401, true);
    }
    if (!silent) recordFailure(fn, detail);
    throw new InvokeError(detail, fn, status);
  }

  return data as T;
}

/** Non-throwing variant for call sites that already branch on { data, error }. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function tryInvoke<T = any>(
  fn: string,
  options: InvokeOptions = {},
): Promise<{ data: T | null; error: InvokeError | null }> {
  try {
    return { data: await invokeFunction<T>(fn, options), error: null };
  } catch (e) {
    const err = e instanceof InvokeError ? e : new InvokeError(String(e), fn);
    return { data: null, error: err };
  }
}
